import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PrinterIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CashIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";

import { useEffect, useState, useRef } from "react";
import swal from "sweetalert";

import { selectCurrentUser } from "../../Features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";

import LoadingBar from "react-top-loading-bar";
import ReactPaginate from "react-paginate";

import "../../Pagination.css";
import "./ManageReceiptsView.css";

import { motion, AnimatePresence } from "framer-motion";

import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { DateRangePicker } from "react-date-range";
import { addDays, subDays } from "date-fns";

import { useGetUserEmployeesQuery } from "../../Features/userEmployeesSlice";

import {
  useGetAllDateRangeUserShiftsMutation,
  useGetAllUserShiftsQuery,
  useGetShiftByIdMutation,
} from "../../Features/employeeShiftLogsSlice";

import {
  selectDateRange,
  setDateRange,
} from "../../Features/date/dateSlice";

import { useTheme } from "../../ThemeContext";

function ManageShiftsView({}) {
  const { darkMode } = useTheme();

  const user = useSelector(selectCurrentUser);
  const date_range = useSelector(selectDateRange);
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);

  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);

  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(0);

  const pickerRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(false);

  const [selectedShift, setSelectedShift] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [cashTotal, setCashTotal] = useState(0);
  const [totalCash, setTotalCash] = useState(0);

  const [shiftSummary, setShiftSummary] = useState({
    total: 0,
    active: 0,
    closed: 0,
  });

  const printRef = useRef();

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: addDays(new Date(), 1),
      key: "selection",
    },
  ]);

  // ==========================================================
  // SHIFT DETAILS
  // ==========================================================

  const [shiftDetails] = useGetShiftByIdMutation();

  const openTicketModal = async (shift_id) => {
    try {
      setProgress(80);

      const resp = await shiftDetails(shift_id).unwrap();

      if (
        resp &&
        resp.data &&
        resp.data.sales_by_payment_type
      ) {
        setSelectedShift(resp.data);
        setIsModalOpen(true);

        const cashOnly =
          resp.data.sales_by_payment_type.filter(
            (item) =>
              item.payment_type === "Cash"
          );

        if (cashOnly.length > 0) {
          const totalCashSales =
            cashOnly.reduce(
              (sum, item) =>
                sum +
                parseFloat(
                  item.net_sales || 0
                ),
              0
            );

          const expectedCash =
            Number(totalCashSales) +
            Number(
              resp.data.current_shift
                .starting_amount || 0
            );

          setCashTotal(totalCashSales);
          setTotalCash(expectedCash);
        } else {
          setCashTotal(0);

          if (
            Number(
              resp.data.current_shift
                .starting_amount
            ) !== 0
          ) {
            setTotalCash(
              Number(
                resp.data.current_shift
                  .starting_amount
              )
            );
          } else {
            setTotalCash(0);
          }
        }
      } else {
        setCashTotal(0);
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        "This shift is still active. Close the shift to view report",
        "error"
      );
    }
  };

  const closeTicketModal = () => {
    setIsModalOpen(false);
    setSelectedShift(null);
    setTotalCash(0);
    setCashTotal(0);
  };

  // ==========================================================
  // DATES
  // ==========================================================

  const initialStartDate = date_range
    ? format(
        date_range.startDate,
        "yyyy-MM-dd"
      )
    : format(
        subDays(new Date(), 7),
        "yyyy-MM-dd"
      );

  const initialEndDate = date_range
    ? format(
        date_range.endDate,
        "yyyy-MM-dd"
      )
    : format(
        new Date(),
        "yyyy-MM-dd"
      );

  // ==========================================================
  // GET SHIFTS
  // ==========================================================

  const {
    data,
    isLoading,
    isSuccess,
  } = useGetAllUserShiftsQuery({
    user_id: user.id,
    pagination: "10",
    start_date: initialStartDate,
    end_date: initialEndDate,
    employee_id: "0",
  });

  // ==========================================================
  // EMPLOYEES
  // ==========================================================

  const { data: employees } =
    useGetUserEmployeesQuery({
      account_id: user.id,
      paginate: 10,
    });

  // ==========================================================
  // INITIAL DATA
  // ==========================================================

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && data?.data) {
      setCurrentItems(
        data.data.shifts.data || []
      );

      setPageCount(
        Math.ceil(
          data.data.shifts.total /
            data.data.shifts.per_page
        )
      );

      setShiftSummary({
        total: Number(data.data.total_shifts || 0),
        active: Number(data.data.active_shifts || 0),
        closed: Number(data.data.closed_shifts || 0),
      });
    }

    setProgress(100);

    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [
    data,
    isLoading,
    isSuccess,
  ]);

  // ==========================================================
  // DATE RANGE MUTATION
  // ==========================================================

  const [fetchCustomData] =
    useGetAllDateRangeUserShiftsMutation();

  // ==========================================================
  // DATE RANGE SELECT
  // ==========================================================

  const handleSelect = async (ranges) => {
    setCurrentPage(0);

    const { selection } = ranges;

    setState([selection]);

    if (!isSelecting) {
      setIsSelecting(true);
      return;
    }

    setIsSelecting(false);
    setOpen(false);

    const startDate = format(
      selection.startDate,
      "yyyy-MM-dd"
    );

    const endDate = format(
      selection.endDate,
      "yyyy-MM-dd"
    );

    dispatch(
      setDateRange({
        date_range: selection,
      })
    );

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    try {
      setProgress(80);

      const resp =
        await fetchCustomData({
          user_id: user.id,
          pagination: numRowsPerPage,
          start_date: startDate,
          end_date: endDate,
          employee_id: selectedEmployee
            ? selectedEmployee
            : "0",
        }).unwrap();

      if (resp.success) {
        setSelectedEmployee("");

        setCurrentItems(
          resp.data.shifts.data || []
        );

        setShiftSummary({
          total: Number(resp.data.total_shifts || 0),
          active: Number(resp.data.active_shifts || 0),
          closed: Number(resp.data.closed_shifts || 0),
        });

        setPageCount(
          Math.ceil(
            resp.data.shifts.total /
              resp.data.shifts.per_page
          )
        );
      } else {
        setShiftSummary({
          total: Number(0),
          active: Number(0),
          closed: Number(0),
        });
        setCurrentItems([]);
        setPageCount(0);

        swal("No results found.");
      }
    } catch (err) {
      setShiftSummary({
          total: Number(0),
          active: Number(0),
          closed: Number(0),
        });
      setCurrentItems([]);
      setPageCount(0);

      swal(
        "Error",
        "Data not found",
        "error"
      );
    } finally {
      setProgress(100);
    }
  };

  // ==========================================================
  // EMPLOYEE FILTER
  // ==========================================================

  const handleEmployeeChange = (
    e,
    employeeId
  ) => {
    submitSearch(e, employeeId);
  };

  // ==========================================================
  // SEARCH
  // ==========================================================

  const submitSearch = async (
    e,
    user_employee_id = false
  ) => {
    setCurrentPage(0);

    if (e?.preventDefault) {
      e.preventDefault();
    }

    const employee_id =
      user_employee_id
        ? user_employee_id
        : "0";

    const startDate =
      selectedStartDate
        ? selectedStartDate
        : initialStartDate;

    const endDate =
      selectedEndDate
        ? selectedEndDate
        : initialEndDate;

    try {
      setProgress(80);

      const resp =
        await fetchCustomData({
          user_id: user.id,
          pagination: numRowsPerPage,
          start_date: startDate,
          end_date: endDate,
          employee_id,
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.shifts.data || []
        );

        setShiftSummary({
          total: Number(resp.data.total_shifts || 0),
          active: Number(resp.data.active_shifts || 0),
          closed: Number(resp.data.closed_shifts || 0),
        });

        setPageCount(
          Math.ceil(
            resp.data.shifts.total /
              resp.data.shifts.per_page
          )
        );
      } else {
        setShiftSummary({
          total: Number(0),
          active: Number(0),
          closed: Number(0),
        });
        setCurrentItems([]);
        setPageCount(0);
      }

      setProgress(100);
    } catch (err) {
      setShiftSummary({
          total: Number(0),
          active: Number(0),
          closed: Number(0),
        });
      setProgress(100);

      setCurrentItems([]);
      setPageCount(0);

      swal("No results found...");
    }
  };

  // ==========================================================
  // CHANGE ROWS PER PAGE
  // ==========================================================

  const [pageChange] =
    useGetAllDateRangeUserShiftsMutation();

  const onChangePageRows = async (
    rows
  ) => {
    setCurrentPage(0);

    const rowsPerPage =
      parseInt(rows);

    const startDate =
      selectedStartDate
        ? selectedStartDate
        : initialStartDate;

    const endDate =
      selectedEndDate
        ? selectedEndDate
        : initialEndDate;

    try {
      setProgress(80);

      const resp =
        await pageChange({
          user_id: user.id,
          pagination: rowsPerPage,
          start_date: startDate,
          end_date: endDate,
          employee_id: selectedEmployee
            ? selectedEmployee
            : "0",
          page: 1,
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.shifts.data || []
        );

        setPageCount(
          Math.ceil(
            resp.data.shifts.total /
              resp.data.shifts.per_page
          )
        );

        setNumRowsPerPage(
          rowsPerPage
        );
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        err.message ||
          "Unable to load shifts",
        "error"
      );
    }
  };

  // ==========================================================
  // PAGE CHANGE
  // ==========================================================

  const handlePageChange = async (
    currentPage
  ) => {
    setCurrentPage(currentPage);

    const pageNum =
      parseInt(currentPage) + 1;

    const startDate =
      selectedStartDate
        ? selectedStartDate
        : initialStartDate;

    const endDate =
      selectedEndDate
        ? selectedEndDate
        : initialEndDate;

    try {
      setProgress(80);

      const resp =
        await pageChange({
          user_id: user.id,
          pagination:
            numRowsPerPage,
          start_date: startDate,
          end_date: endDate,
          employee_id:
            selectedEmployee
              ? selectedEmployee
              : "0",
          page: pageNum,
        }).unwrap();

      if (resp.success) {
        setCurrentItems(
          resp.data.shifts.data || []
        );

        setPageCount(
          Math.ceil(
            resp.data.shifts.total /
              resp.data.shifts.per_page
          )
        );
      }

      setProgress(100);
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        "An unexpected error occurred. Try again later",
        "error"
      );
    }
  };

  // ==========================================================
  // MONEY FORMAT
  // ==========================================================

  const formatMoney = (value) => {
    return Number(
      value ?? 0
    ).toLocaleString("en-ZM", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ==========================================================
  // DIFFERENCE
  // ==========================================================

  const getExpectedCash = (shift) => {
    return Number(
      shift?.expected_cash || 0
    ) === 0
      ? Number(
          shift?.starting_amount || 0
        )
      : Number(
          shift?.expected_cash || 0
        );
  };

  const getDifference = (shift) => {
    return (
      Number(
        shift?.actual_cash || 0
      ) - getExpectedCash(shift)
    );
  };

  // ==========================================================
  // CSV EXPORT
  // ==========================================================

  const handleExportCSV = async () => {
    const employee_id =
      selectedEmployee
        ? selectedEmployee
        : "0";

    const startDate =
      selectedStartDate
        ? selectedStartDate
        : initialStartDate;

    const endDate =
      selectedEndDate
        ? selectedEndDate
        : initialEndDate;

    try {
      setIsExporting(true);

      const response =
        await fetch(
          `http://127.0.0.1:8000/user/user-shifts/${user.id}/100000/${startDate}/${endDate}/${employee_id}`
        );

      const result =
        await response.json();

      if (
        !result.success ||
        !result.data?.data?.length
      ) {
        swal(
          "No data",
          "No shift records found to export",
          "info"
        );

        setIsExporting(false);
        return;
      }

      const logs =
        result.data.data;

      const headers = [
        "Opened At",
        "Closed At",
        "Employee Name",
        "Employee ID",
        "Expected Cash",
        "Actual Cash",
        "Difference",
      ];

      const escapeCsvValue = (
        value
      ) => {
        return `"${String(
          value ?? ""
        ).replace(/"/g, '""')}"`;
      };

      const rows = logs.map(
        (item) => {
          const expected =
            Number(
              item.expected_cash ?? 0
            );

          const actual =
            Number(
              item.actual_cash ?? 0
            );

          const difference =
            actual -
            (expected === 0
              ? Number(
                  item.starting_amount ??
                    0
                )
              : expected);

          return [
            item.created_at || "",
            item.updated_at || "",
            `${item.employee_fname || ""} ${
              item.employee_lname || ""
            }`,
            item.employee_id || "",
            expected.toFixed(2),
            actual.toFixed(2),
            difference.toFixed(2),
          ];
        }
      );

      const csvContent = [
        headers,
        ...rows,
      ]
        .map((row) =>
          row
            .map(escapeCsvValue)
            .join(",")
        )
        .join("\n");

      const blob = new Blob(
        [csvContent],
        {
          type: "text/csv;charset=utf-8;",
        }
      );

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement(
          "a"
        );

      link.href = url;

      link.download = `shift_summary_${new Date()
        .toISOString()
        .split("T")[0]}.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      setIsExporting(false);
    } catch (error) {
      console.error(
        "Shift CSV Export Error:",
        error
      );

      swal(
        "Error",
        "Something went wrong while exporting",
        "error"
      );

      setIsExporting(false);
    }
  };

  // ==========================================================
  // PRINT SHIFT REPORT
  // ==========================================================

  const handlePrint = () => {
    if (!printRef.current) {
      return;
    }

    const printContents =
      printRef.current.innerHTML;

    const printWindow =
      window.open(
        "",
        "",
        "width=400,height=700"
      );

    if (!printWindow) {
      swal(
        "Print Error",
        "Please allow popups to print the shift report.",
        "error"
      );
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Shift Report</title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              font-family: monospace;
              font-size: 13px;
              color: #000;
              padding: 4px;
              margin: 0;
            }

            .ticket {
              width: 270px;
              margin: auto;
            }

            .flex {
              display: flex;
              justify-content: space-between;
              gap: 5px;
            }

            .text-center {
              text-align: center;
            }

            .font-bold {
              font-weight: bold;
            }

            .uppercase {
              text-transform: uppercase;
            }

            .border-dashed {
              border-top: 1px dashed #777;
              border-bottom: 1px dashed #777;
            }

            .border-top {
              border-top: 1px dashed #777;
            }

            .border-bottom {
              border-bottom: 1px dashed #777;
            }

            .green {
              color: #80b643;
            }

            .mb-1 {
              margin-bottom: 4px;
            }

            .mt-1 {
              margin-top: 4px;
            }

            .mt-2 {
              margin-top: 8px;
            }

            .pt-1 {
              padding-top: 4px;
            }

            .pb-1 {
              padding-bottom: 4px;
            }
          </style>
        </head>

        <body>
          ${printContents}
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  // ==========================================================
  // DISPLAY DATES
  // ==========================================================

  const displayedStartDate =
    date_range
      ? date_range.startDate
      : state[0].startDate;

  const displayedEndDate =
    date_range
      ? date_range.endDate
      : state[0].endDate;

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() =>
          setProgress(0)
        }
      />

      <div className="w-full px-3 sm:px-5 lg:px-6 pt-5">

        {/* ====================================================
            PAGE HEADER
        ===================================================== */}

        <div className="flex items-center gap-3 mb-5">

          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 shrink-0">
            <ClockIcon className="w-6 h-6 text-[#80b643]" />
          </div>

          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Manage Shifts
            </h1>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              View employee shifts, cash balances and shift reports
            </p>
          </div>

        </div>

        {/* ====================================================
            FILTER CARD
        ===================================================== */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 mb-5">

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">

            {/* DATE */}

            <div
              ref={pickerRef}
              className="relative w-full lg:w-auto"
            >

              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Report Period
              </label>

              <button
                type="button"
                onClick={() =>
                  setOpen(!open)
                }
                className="w-full lg:w-auto min-w-[250px] flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 px-3 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:border-[#80b643] focus:outline-none transition"
              >

                <div className="flex items-center gap-2">

                  <CalendarIcon className="w-4 h-4 text-[#80b643]" />

                  <span>
                    {format(
                      displayedStartDate,
                      "MMM dd, yyyy"
                    )}{" "}
                    →{" "}
                    {format(
                      displayedEndDate,
                      "MMM dd, yyyy"
                    )}
                  </span>

                </div>

                <ChevronDownIcon
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    open
                      ? "rotate-180"
                      : ""
                  }`}
                />

              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    ref={pickerRef}
                    initial={{
                      opacity: 0,
                      y: -8,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      y: -8,
                    }}
                    transition={{
                      duration: 0.2,
                    }}
                    className="absolute z-50 mt-2 left-0 bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 rounded-xl p-2 overflow-hidden"
                  >
                    <DateRangePicker
                      onChange={
                        handleSelect
                      }
                      showSelectionPreview={
                        true
                      }
                      moveRangeOnFirstSelection={
                        false
                      }
                      months={1}
                      ranges={state}
                      direction="horizontal"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

            </div>

            {/* EMPLOYEE */}

            <div className="w-full lg:w-auto">

              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Employee
              </label>

              <div className="relative min-w-[230px]">

                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#80b643] pointer-events-none" />

                <select
                  value={
                    selectedEmployee
                  }
                  onChange={(e) => {
                    setSelectedEmployee(
                      e.target.value
                    );

                    handleEmployeeChange(
                      e,
                      e.target.value
                    );
                  }}
                  className="appearance-none w-full bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 pl-9 pr-9 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:border-[#80b643] focus:outline-none focus:border-[#80b643] cursor-pointer transition"
                >

                  <option value="">
                    All Employees
                  </option>

                  {employees?.data?.data?.map(
                    (item) => (
                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.fname}{" "}
                        {item.lname}
                      </option>
                    )
                  )}

                </select>

                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

              </div>

            </div>

          </div>

        </div>

        {/* ====================================================
            SUMMARY CARDS
        ===================================================== */}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">

          {/* TOTAL SHIFTS */}

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4">

            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <ChartBarIcon className="w-5 h-5 text-[#80b643]" />
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Shifts
                </p>

                <p className="mt-1 text-xl sm:text-2xl font-semibold text-gray-700">
                  {shiftSummary.total}
                </p>
              </div>

            </div>

          </div>

          {/* CLOSED */}

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4">

            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                <ClockIcon className="w-5 h-5 text-[#80b643]" />
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Closed Shifts
                </p>

                <p className="mt-1 text-xl sm:text-2xl font-semibold text-gray-700">
                  {shiftSummary.closed}
                </p>
              </div>

            </div>

          </div>

          {/* ACTIVE */}

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4">

            <div className="flex items-center gap-3">

              <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <CashIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </div>

              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Active Shifts
                </p>

                <p className="mt-1 text-xl sm:text-2xl font-semibold text-gray-700 dark:text-gray-200">
                  {shiftSummary.active}
                </p>
              </div>

            </div>

          </div>

        </div>

        {/* ====================================================
            TABLE CARD
        ===================================================== */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {/* TABLE HEADER */}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-4 border-b border-gray-200 dark:border-gray-800">

            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Shift Transactions
              </h2>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Review employee shift openings, closures and cash differences
              </p>
            </div>

            <button
              type="button"
              onClick={
                handleExportCSV
              }
              disabled={
                isExporting ||
                currentItems.length ===
                  0
              }
              className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium shadow-sm transition ${
                isExporting ||
                currentItems.length ===
                  0
                  ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-700 cursor-not-allowed"
                  : "bg-[#80b643] hover:bg-[#719f3a] text-white border-[#80b643]"
              }`}
            >

              <PrinterIcon className="w-4 h-4" />

              {isExporting
                ? "Exporting..."
                : "Export CSV"}

            </button>

          </div>

          {/* ==================================================
              TABLE
          =================================================== */}

          {currentItems.length > 0 ? (
            <div className="overflow-x-auto">

              <table className="w-full min-w-[1050px]">

                <thead className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800">

                  <tr>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Opening Time
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Closing Time
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Employee
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Expected Cash
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Actual Cash
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Difference
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Status
                    </th>

                  </tr>

                </thead>

                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                  {currentItems.map(
                    (item) => {
                      const difference =
                        getDifference(
                          item
                        );

                      const isClosed =
                        Number(
                          item.status
                        ) === 1;

                      return (
                        <tr
                          key={item.id}
                          onClick={() =>
                            openTicketModal(
                              item.id
                            )
                          }
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition"
                        >

                          {/* OPENING */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <div className="flex items-center gap-2">

                              <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <ClockIcon className="w-4 h-4 text-[#80b643]" />
                              </div>

                              <span className="text-sm text-gray-700 dark:text-gray-200">
                                {item.created_at}
                              </span>

                            </div>

                          </td>

                          {/* CLOSING */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              {isClosed
                                ? item.updated_at
                                : "--"}
                            </span>

                          </td>

                          {/* EMPLOYEE */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <div className="flex items-center gap-3">

                              <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <UserIcon className="w-4 h-4 text-[#80b643]" />
                              </div>

                              <div>

                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {
                                    item.employee_fname
                                  }{" "}
                                  {
                                    item.employee_lname
                                  }
                                </p>

                                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                  Employee #
                                  {
                                    item.employee_id
                                  }
                                </p>

                              </div>

                            </div>

                          </td>

                          {/* EXPECTED */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              K{" "}
                              {formatMoney(
                                item.expected_cash
                              )}
                            </span>

                          </td>

                          {/* ACTUAL */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              K{" "}
                              {formatMoney(
                                item.actual_cash
                              )}
                            </span>

                          </td>

                          {/* DIFFERENCE */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                                difference < 0
                                  ? "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                                  : "bg-[#80b643]/10 text-[#80b643]"
                              }`}
                            >
                              {difference < 0
                                ? "-"
                                : "+"}
                              K{" "}
                              {formatMoney(
                                Math.abs(
                                  difference
                                )
                              )}
                            </span>

                          </td>

                          {/* STATUS */}

                          <td className="px-5 py-4 whitespace-nowrap">

                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                isClosed
                                  ? "bg-[#80b643]/10 text-[#80b643]"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                  isClosed
                                    ? "bg-[#80b643]"
                                    : "bg-gray-400"
                                }`}
                              />

                              {isClosed
                                ? "Closed"
                                : "Active"}
                            </span>

                          </td>

                        </tr>
                      );
                    }
                  )}

                </tbody>

              </table>

            </div>
          ) : (

            /* ==================================================
               EMPTY STATE
            =================================================== */

            <div className="flex flex-col items-center justify-center py-16 px-4">

              <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                <ClockIcon className="w-7 h-7 text-gray-400 dark:text-gray-500" />

              </div>

              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                No shifts found
              </h3>

              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center max-w-sm">
                There are no employee shifts available for the selected period or employee.
              </p>

            </div>
          )}

          {/* ==================================================
              PAGINATION
          =================================================== */}

          {currentItems.length >
            0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">

              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {currentItems.length}
                </span>{" "}
                shifts
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4">

                <ReactPaginate
                  breakLabel="..."
                  nextLabel={
                    <ChevronRightIcon className="h-5 w-5" />
                  }
                  previousLabel={
                    <ChevronLeftIcon className="h-5 w-5" />
                  }
                  onPageChange={(e) =>
                    handlePageChange(
                      e.selected
                    )
                  }
                  pageRangeDisplayed={3}
                  pageCount={pageCount}
                  forcePage={currentPage}
                  renderOnZeroPageCount={
                    null
                  }
                  containerClassName="pagination"
                  pageLinkClassName="page-num"
                  previousLinkClassName="controlBtn"
                  nextLinkClassName="controlBtn"
                  activeLinkClassName={`${
                    darkMode
                      ? ""
                      : "active"
                  } dark:text-white bg-white dark:bg-gray-700 hover:dark:text-white hover:dark:bg-gray-600`}
                />

                <div className="flex items-center gap-2">

                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Rows per page
                  </span>

                  <select
                    value={
                      numRowsPerPage
                    }
                    onChange={(e) =>
                      onChangePageRows(
                        e.target.value
                      )
                    }
                    className="w-20 px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-200 focus:outline-none focus:border-[#80b643]"
                  >
                    <option value="10">
                      10
                    </option>

                    <option value="25">
                      25
                    </option>

                    <option value="50">
                      50
                    </option>

                    <option value="100">
                      100
                    </option>
                  </select>

                </div>

              </div>

            </div>
          )}

        </div>

      </div>

      {/* ======================================================
          SHIFT REPORT MODAL
      ======================================================= */}

      <AnimatePresence>
        {isModalOpen &&
          selectedShift && (
            <>
              {/* OVERLAY */}

              <motion.div
                className="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-40"
                onClick={
                  closeTicketModal
                }
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
              />

              {/* MODAL */}

              <motion.div
                initial={{
                  opacity: 0,
                  y: 30,
                  scale: 0.97,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  y: 30,
                  scale: 0.97,
                }}
                transition={{
                  duration: 0.2,
                }}
                className="fixed inset-x-3 bottom-3 sm:inset-auto sm:bottom-6 sm:right-6 z-50 bg-white dark:bg-gray-900 text-gray-800 dark:text-white shadow-2xl rounded-2xl w-auto sm:w-[430px] max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-800"
              >

                {/* MODAL HEADER */}

                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">

                  <div className="flex items-center gap-3">

                    <div className="w-10 h-10 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                      <ClockIcon className="w-5 h-5 text-[#80b643]" />

                    </div>

                    <div>

                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Shift Report
                      </h2>

                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Employee shift summary
                      </p>

                    </div>

                  </div>

                  <button
                    onClick={
                      closeTicketModal
                    }
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition"
                  >
                    <span className="text-lg leading-none">
                      ×
                    </span>
                  </button>

                </div>

                {/* MODAL CONTENT */}

                <div className="overflow-y-auto max-h-[calc(90vh-130px)] px-5 py-4">

                  {/* EMPLOYEE */}

                  <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60">

                    <div className="w-10 h-10 rounded-full bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                      <UserIcon className="w-5 h-5 text-[#80b643]" />

                    </div>

                    <div>

                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {
                          selectedShift
                            .current_shift
                            .fname
                        }{" "}
                        {
                          selectedShift
                            .current_shift
                            .lname
                        }
                      </p>

                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Shift employee
                      </p>

                    </div>

                  </div>

                  {/* SHIFT TIMES */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">

                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3">

                      <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">
                        Shift Opened
                      </p>

                      <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {
                          selectedShift
                            .current_shift
                            .created_at
                        }
                      </p>

                    </div>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-3">

                      <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">
                        Shift Closed
                      </p>

                      <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {
                          selectedShift
                            .current_shift
                            .updated_at
                        }
                      </p>

                    </div>

                  </div>

                  {/* TICKETS */}

                  <div className="flex items-center justify-between py-3 border-y border-gray-200 dark:border-gray-800 mb-4">

                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Total tickets sold
                    </span>

                    <span className="text-sm font-semibold text-[#80b643]">
                      {
                        selectedShift.total_tickets_sold
                      }
                    </span>

                  </div>

                  {/* CASH DRAWER */}

                  <div className="mb-5">

                    <div className="flex items-center gap-2 mb-3">

                      <div className="w-7 h-7 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                        <CashIcon className="w-4 h-4 text-[#80b643]" />

                      </div>

                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#80b643]">
                        Cash Drawer
                      </h3>

                    </div>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Starting cash
                        </span>

                        <span className="text-xs font-medium">
                          {selectedShift.current_shift.starting_amount
                            ? `K${formatMoney(
                                selectedShift
                                  .current_shift
                                  .starting_amount
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Cash sales
                        </span>

                        <span className="text-xs font-medium">
                          {cashTotal
                            ? `K${formatMoney(
                                cashTotal
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Cash refunds
                        </span>

                        <span className="text-xs font-medium">
                          {selectedShift.cash_sales_returns
                            ? `K${formatMoney(
                                selectedShift.cash_sales_returns
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Expected cash
                        </span>

                        <span className="text-xs font-semibold text-gray-700">
                          {totalCash
                            ? `K${formatMoney(
                                totalCash
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Actual cash
                        </span>

                        <span className="text-xs font-semibold">
                          {selectedShift.current_shift.actual_cash
                            ? `K${formatMoney(
                                selectedShift
                                  .current_shift
                                  .actual_cash
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-3 bg-gray-50 dark:bg-gray-800/60">

                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                          Difference
                        </span>

                        <span
                          className={`text-xs font-bold ${
                            getDifference(
                              selectedShift.current_shift
                            ) < 0
                              ? "text-gray-500 dark:text-gray-300"
                              : "text-gray-700"
                          }`}
                        >
                          {getDifference(
                            selectedShift.current_shift
                          ) < 0
                            ? "-"
                            : "+"}
                          K
                          {formatMoney(
                            Math.abs(
                              getDifference(
                                selectedShift.current_shift
                              )
                            )
                          )}
                        </span>

                      </div>

                    </div>

                  </div>

                  {/* SALES SUMMARY */}

                  <div className="mb-5">

                    <div className="flex items-center gap-2 mb-3">

                      <div className="w-7 h-7 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">

                        <ChartBarIcon className="w-4 h-4 text-[#80b643]" />

                      </div>

                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#80b643]">
                        Sales Summary
                      </h3>

                    </div>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Gross sales
                        </span>

                        <span className="text-xs font-semibold">
                          {selectedShift.gross_sales
                            ? `K${formatMoney(
                                selectedShift.gross_sales
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Refunds
                        </span>

                        <span className="text-xs">
                          {selectedShift.sales_returns
                            ? `K${formatMoney(
                                selectedShift.sales_returns
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Discounts
                        </span>

                        <span className="text-xs">
                          {selectedShift.total_discounts
                            ? `K${formatMoney(
                                selectedShift.total_discounts
                              )}`
                            : "--"}
                        </span>

                      </div>

                      <div className="flex justify-between px-3 py-3 bg-[#80b643]/5 dark:bg-[#80b643]/10">

                        <span className="text-xs font-semibold">
                          Net sales
                        </span>

                        <span className="text-sm font-bold text-gray-700">
                          {selectedShift.net_sales
                            ? `K${formatMoney(
                                selectedShift.net_sales
                              )}`
                            : "--"}
                        </span>

                      </div>

                    </div>

                  </div>

                  {/* PAYMENT TYPE */}

                  <div className="mb-5">

                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[#80b643] mb-3">
                      Payment Summary
                    </h3>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">

                      <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/60 text-[9px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">

                        <span>
                          Payment
                        </span>

                        <span className="text-right">
                          Refunds
                        </span>

                        <span className="text-right">
                          Discounts
                        </span>

                        <span className="text-right">
                          Net
                        </span>

                      </div>

                      {selectedShift?.sales_by_payment_type?.map(
                        (
                          item,
                          index
                        ) => (
                          <div
                            key={index}
                            className="grid grid-cols-4 gap-2 px-3 py-2.5 border-t border-gray-100 dark:border-gray-800 text-xs"
                          >

                            <span className="truncate">
                              {
                                item.payment_type
                              }
                            </span>

                            <span className="text-right text-gray-500">
                              {item.returns
                                ? `K${formatMoney(
                                    item.returns
                                  )}`
                                : "--"}
                            </span>

                            <span className="text-right text-gray-500">
                              {item.discounts
                                ? `K${formatMoney(
                                    item.discounts
                                  )}`
                                : "--"}
                            </span>

                            <span className="text-right font-medium">
                              {item.net_sales
                                ? `K${formatMoney(
                                    item.net_sales
                                  )}`
                                : "--"}
                            </span>

                          </div>
                        )
                      )}

                      <div className="grid grid-cols-4 gap-2 px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 font-semibold text-xs">

                        <span>
                          Sub Total
                        </span>

                        <span className="text-right">
                          {selectedShift.sales_returns
                            ? `K${formatMoney(
                                selectedShift.sales_returns
                              )}`
                            : "--"}
                        </span>

                        <span className="text-right">
                          {selectedShift.total_discounts
                            ? `K${formatMoney(
                                selectedShift.total_discounts
                              )}`
                            : "--"}
                        </span>

                        <span className="text-right text-gray-700">
                          {selectedShift.net_sales
                            ? `K${formatMoney(
                                selectedShift.net_sales
                              )}`
                            : "--"}
                        </span>

                      </div>

                    </div>

                  </div>

                  {/* TAX SUMMARY */}

                  <div>

                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[#80b643] mb-3">
                      Tax Summary
                    </h3>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">

                      {selectedShift?.tax_by_tax_name
                        ?.filter(
                          (item) =>
                            item.tax_name !==
                            "No Tax"
                        )
                        .map(
                          (
                            item,
                            index
                          ) => (
                            <div
                              key={index}
                              className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800"
                            >

                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {
                                  item.tax_name
                                }{" "}
                                (
                                {
                                  item.tax_rate
                                }
                                %)
                              </span>

                              <span className="text-xs font-medium">
                                {item.total_tax
                                  ? `K${formatMoney(
                                      item.total_tax
                                    )}`
                                  : "--"}
                              </span>

                            </div>
                          )
                        )}

                      <div className="flex items-center justify-between px-3 py-3 bg-[#80b643]/5 dark:bg-[#80b643]/10">

                        <span className="text-xs font-semibold">
                          Total Tax
                        </span>

                        <span className="text-sm font-bold text-gray-700">
                          {selectedShift.total_tax
                            ? `K${formatMoney(
                                selectedShift.total_tax
                              )}`
                            : "--"}
                        </span>

                      </div>

                    </div>

                  </div>

                </div>

                {/* MODAL ACTIONS */}

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/30">

                  <button
                    type="button"
                    onClick={
                      closeTicketModal
                    }
                    className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  >
                    Close
                  </button>

                  <button
                    type="button"
                    onClick={
                      handlePrint
                    }
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-[#80b643] hover:bg-[#719f3a] shadow-sm transition"
                  >
                    <PrinterIcon className="w-4 h-4" />
                    Print
                  </button>

                </div>

              </motion.div>
            </>
          )}
      </AnimatePresence>

      {/* ======================================================
          HIDDEN PRINT VERSION
      ======================================================= */}

      {selectedShift && (
        <div
          ref={printRef}
          className="hidden"
        >

          <div className="ticket text-[14px] font-mono leading-tight">

            <div className="text-center font-bold uppercase mb-2 tracking-wide">
              SHIFT REPORT
            </div>

            <div className="border-t border-dashed border-gray-400 my-1" />

            {/* SHIFT INFORMATION */}

            <div className="mb-1">

              <div className="flex justify-between">
                <span>
                  Shift Opened:
                </span>

                <span>
                  {
                    selectedShift
                      .current_shift
                      .created_at
                  }
                </span>
              </div>

              <div className="flex justify-between">
                <span>
                  Opened By:
                </span>

                <span className="uppercase">
                  {
                    selectedShift
                      .current_shift
                      .fname
                  }{" "}
                  {
                    selectedShift
                      .current_shift
                      .lname
                  }
                </span>
              </div>

              <div className="flex justify-between">
                <span>
                  Shift Closed:
                </span>

                <span>
                  {
                    selectedShift
                      .current_shift
                      .updated_at
                  }
                </span>
              </div>

              <div className="flex justify-between border-b border-dashed border-gray-400 pb-1">

                <span>
                  Closed By:
                </span>

                <span className="uppercase">
                  {
                    selectedShift
                      .current_shift
                      .fname
                  }{" "}
                  {
                    selectedShift
                      .current_shift
                      .lname
                  }
                </span>

              </div>

            </div>

            {/* CASH DRAWER */}

            <div className="uppercase font-bold text-center my-1 border-t border-dashed border-gray-400 pt-1">
              CASH DRAWER
            </div>

            <div className="flex justify-between">
              <span>
                Starting Cash:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift
                    .current_shift
                    .starting_amount
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span>
                Cash Sales:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  cashTotal
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span>
                Cash Refunds:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift.cash_sales_returns
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span>
                Expected Cash:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  totalCash
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span>
                Actual Cash:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift
                    .current_shift
                    .actual_cash
                )}
              </span>
            </div>

            <div className="flex justify-between border-b border-dashed border-gray-400 pb-1">

              <span>
                Difference:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  getDifference(
                    selectedShift.current_shift
                  )
                )}
              </span>

            </div>

            {/* SALES SUMMARY */}

            <div className="uppercase font-bold text-center mt-2 border-t border-dashed border-gray-400 pt-1">
              SALES SUMMARY
            </div>

            <div className="flex justify-between">
              <span>
                Gross Total:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift.gross_sales
                )}
              </span>
            </div>

            {selectedShift?.sales_by_payment_type?.map(
              (
                item,
                index
              ) => (
                <div
                  key={index}
                  className="flex justify-between"
                >
                  <span>
                    {
                      item.payment_type
                    }
                  </span>

                  <span>
                    K{" "}
                    {formatMoney(
                      item.net_sales
                    )}
                  </span>
                </div>
              )
            )}

            <div className="flex justify-between">
              <span>
                Refunds:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift.sales_returns
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span>
                Discounts:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift.total_discounts
                )}
              </span>
            </div>

            <div className="flex justify-between border-b border-dashed border-gray-400 pb-1 font-bold">

              <span>
                Net Sales:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift.net_sales
                )}
              </span>

            </div>

            {/* TAX */}

            {selectedShift?.tax_by_tax_name
              ?.filter(
                (item) =>
                  item.tax_name !==
                  "No Tax"
              )
              .map(
                (
                  item,
                  index
                ) => (
                  <div
                    key={index}
                    className="flex justify-between"
                  >
                    <span>
                      {
                        item.tax_name
                      }{" "}
                      (
                      {
                        item.tax_rate
                      }
                      %)
                    </span>

                    <span>
                      K{" "}
                      {formatMoney(
                        item.total_tax
                      )}
                    </span>
                  </div>
                )
              )}

            <div className="flex justify-between font-bold border-t border-dashed border-gray-400 mt-1 pt-1">

              <span>
                Total Tax:
              </span>

              <span>
                K{" "}
                {formatMoney(
                  selectedShift.total_tax
                )}
              </span>

            </div>

            <div className="text-center text-[12px] mt-2 border-t border-dashed border-gray-400 pt-1">
              Shift Report
            </div>

          </div>

        </div>
      )}

    </div>
  );
}

export default ManageShiftsView;